/** Angular imports */
import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { catchError, EMPTY } from 'rxjs';

/** Component imports */
import { TabsComponent } from '@features/playlists/components/single-playlist/shared/tabs/tabs.component';
import { ThumbnailItemComponent } from '@ntv360/component-pantry';

/** Local imports */
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddContentStepsService, MediaLibraryService } from '@core';
import { ApiContent } from '@core/services/media-library/media-library.interface';
import { UiThumbnailContent } from '@features/media-library/interfaces';
import { ADD_CONTENT_SELECT_ASSETS_TAB } from '@features/playlists/constants';
import { ClientTypeName, FilterOperator, SearchbarComponent } from '@shared';
import { FileSizePipe } from '@shared/pipes';
import {
    getContentType,
    getUserTraits,
    isConversionPending,
    Logger,
    searchHandler,
    withLoading,
} from '@shared/utils';

@Component({
    selector: 'app-select-assets-modal',
    imports: [ThumbnailItemComponent, SearchbarComponent, TabsComponent],
    templateUrl: './select-assets.component.html',
    styleUrl: './select-assets.component.scss',
})
export class SelectAssetsComponent {
    /** Manages auto-unscription when component is destroyed */
    private destroyRef = inject(DestroyRef);

    /** Injected add content steps service for steps validation */
    private addContentStepsService = inject(AddContentStepsService);

    /** Injected media library service for fetching media assets */
    private mediaLibraryService = inject(MediaLibraryService);

    /** Controls the visibility of channels tab based on the selected content type */
    public selectedContentType = input.required<'single' | 'block'>();

    /** Dealer ID passed from consuming component if we were to fetch dealer specific data */
    public dealerId = input<string | null>(null);

    /** Controls the visibility of the tabs */
    public showTabs = input<boolean>(true);

    /** Holds the debounced search term for searching contents by its filename. Partial or full */
    protected searchTerm = signal<string>('');

    /** Holds the current selected/viewing tab */
    protected currentTab = signal<number>(0);

    /** Holds the contents data */
    protected contents = signal<UiThumbnailContent[]>([]);

    /** Holds the selected content ids */
    protected selectedIds = signal<Set<string>>(new Set());

    /** Holds whether the contents are being fetched */
    protected isLoadingContents = signal<boolean>(false);

    /** Holds the current page for pagination */
    protected page = signal<number>(1);

    /** Holds the limit per page */
    protected limit = signal<number>(30);

    /** Holds whether there are more pages to load */
    protected hasMore = signal<boolean>(true);

    /** Holds whether it's currently loading more contents via infinite scroll */
    protected isScrolling = signal<boolean>(false);

    /** Marks whether the initial empty-search emission has already been consumed. */
    private hasSeenInitialSearchEmission = false;

    /** Dynamically render or hide the channels tab based on the selected content type */
    protected tabs = computed(() => {
        return this.selectedContentType() === 'block'
            ? ADD_CONTENT_SELECT_ASSETS_TAB
            : ADD_CONTENT_SELECT_ASSETS_TAB.slice(0, ADD_CONTENT_SELECT_ASSETS_TAB.length - 1);
    });

    /** Emits the selected contents via parent to child trigger/invocation */
    public addContents = output<any>();

    constructor() {
        // Reset page when tab, search term, or showBothDealerAndFloatingContents changes
        effect(() => {
            this.currentTab();
            this.searchTerm();
            untracked(() => {
                this.page.set(1);
                this.contents.set([]);
                this.hasMore.set(true);
            });
        });

        // Fetch contents on initial load and on tab change
        effect(() => {
            const selectedTab = this.currentTab();
            const tabsVisible = this.showTabs();
            const page = this.page();
            const dealerId = this.dealerId();
            const userTrait = getUserTraits();

            untracked(() => {
                if (tabsVisible) {
                    this.loadContents(selectedTab, page > 1);
                } else {
                    if (userTrait?.data.clientTypeName === ClientTypeName.DEALER) {
                        if (userTrait?.data.userId) {
                            this.loadDealerContents(userTrait.data.userId, page > 1);
                        } else {
                            Logger.error(
                                'SelectAssetsModal',
                                'Dealer ID is required but you\re logged in as a Dealer',
                            );
                            return;
                        }
                    } else {
                        this.loadFloatingAndDealerContents(dealerId, page > 1);
                    }
                }
            });
        });

        // Effect to load selectedIds from draftData if there are
        effect(() => {
            const draftData = this.addContentStepsService.getDraft();
            if (
                draftData?.selectedContents?.selectedItems &&
                draftData?.selectedContents?.selectedItems.length > 0
            ) {
                this.selectedIds.set(
                    new Set(draftData.selectedContents.selectedItems.map((item) => item.contentId)),
                );
            }
        });

        // Set the validity of the current step (step index 0) based on the selected content ids
        effect(() => {
            const selectedIds = this.selectedIds();
            this.addContentStepsService.setStepValidity(0, selectedIds.size > 0);
        });

        /**
         * Search handler to search for contents by their filename
         * Partial or full filename
         */
        searchHandler(
            this.searchTerm,
            (searchTerm) => {
                return this.mediaLibraryService
                    .searchContentByFileName(
                        searchTerm,
                        getUserTraits()?.data.clientTypeName === ClientTypeName.DEALER
                            ? getUserTraits()?.data.userId
                            : undefined,
                    )
                    .pipe(withLoading(this.isLoadingContents));
            },
            this.destroyRef,
            {
                next: (res) => {
                    this.mapDataToUi(res.data);
                    this.hasMore.set(res.meta.pagination.hasNextPage);
                },
                onEmpty: () => {
                    // Skip the synchronous initial empty emission from toObservable;
                    // the load effect already triggers the initial fetch.
                    if (!this.hasSeenInitialSearchEmission) {
                        this.hasSeenInitialSearchEmission = true;
                        return;
                    }

                    if (this.showTabs()) {
                        this.loadContents(this.currentTab());
                    } else {
                        const userTrait = getUserTraits();
                        if (userTrait?.data.clientTypeName === ClientTypeName.DEALER) {
                            if (userTrait?.data.userId) {
                                this.loadDealerContents(userTrait.data.userId);
                            } else {
                                Logger.error(
                                    'SelectAssetsModal',
                                    'Dealer ID is required but you\re logged in as a Dealer',
                                );
                                return;
                            }
                        } else {
                            this.loadFloatingAndDealerContents(this.dealerId());
                        }
                    }
                },
            },
        );
    }

    /**
     * Handles checking/unchecking content checkbox (thumbnail item checkbox)
     */
    protected onSelectionToggle(contentId: string): void {
        const content = this.contents().find((c) => c.contentId === contentId);
        if (content && this.isPendingConversion(content)) {
            return;
        }

        const currentSelectedIds = this.selectedIds();
        if (currentSelectedIds.has(contentId)) {
            this.selectedIds.set(new Set([...currentSelectedIds].filter((id) => id !== contentId)));
        } else {
            this.selectedIds.set(new Set([...currentSelectedIds, contentId]));
        }
    }

    /**
     * Handles the tab click event
     *
     * @param tabId - The selected tab id to switch to
     */
    protected onTabClick(tabId: number): void {
        this.currentTab.set(tabId);
    }

    /**
     * Public method to save the current data to the draft (in-memory only)
     */
    public saveDataToDraft(): void {
        const selectedItems = this.contents().filter((content) =>
            this.selectedIds().has(content.id),
        );
        this.addContentStepsService.updateDraft('selectedContents', {
            selectedItems,
        });
    }

    /**
     * A public method to emit the selected contents via parent invocation
     */
    public emitSelectedContents(): void {
        const selectedContents = this.contents().filter((content) =>
            this.selectedIds().has(content.id),
        );
        this.addContents.emit(selectedContents);
    }

    /**
     * Maps API response to UI requirements
     *
     * @param contents - The contents data from API response
     * @param append - Whether to append to existing contents or replace
     */
    private mapDataToUi(contents: ApiContent[], append = false): void {
        const mapped = contents.map((item) => {
            const { fileType, ...rest } = item;
            return {
                ...rest,
                id: item.contentId,
                name: item.title ?? item.fileName,
                ownerName: item.ownerName,
                type: getContentType(item.fileType),
                src: item.url,
                size: new FileSizePipe().transform(item.fileSize),
                duration: item.duration?.toString(),
                modified: item.createdAt ? new Date(item.createdAt) : undefined,
                createdAt: item.createdAt,
            } as UiThumbnailContent;
        });

        if (append) {
            this.contents.update((current) => [...current, ...mapped]);
        } else {
            this.contents.set(mapped);
        }
    }

    /** ============================ API Calls ================================== */

    /**
     * Retrieves media library assets tailored to the active tab.
     * Applies dealer-specific filters when the primary tab (ID: 0) is selected.
     * @param tabId - The unique identifier of the selected view tab.
     * @param append - Whether to append the new data to the existing contents list.
     */
    private loadContents(tabId: number, append = false): void {
        //  Construct query filters params
        const queryFiltersParams: Record<string, FilterOperator> = {
            ownerId: {
                _eq: this.dealerId(),
            },
        };
        const filtersByTab = tabId === 0 ? queryFiltersParams : {};

        const loadingSignal = append ? this.isScrolling : this.isLoadingContents;

        this.mediaLibraryService
            .getContents({
                filters: filtersByTab,
                page: this.page(),
                limit: this.limit(),
            })
            .pipe(
                withLoading(loadingSignal),
                catchError((err) => {
                    Logger.error('AddContent', 'Error fetching contents', err);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((res) => {
                this.mapDataToUi(res.data, append);
                this.hasMore.set(res.meta.pagination.hasNextPage);
            });
    }

    /**
     * Handles infinite scroll when user reaches bottom of the container
     *
     * @param event - The scroll event from the container
     */
    protected onScroll(event: Event): void {
        const heightThreshold = 100;
        const element = event.target as HTMLElement;
        const atBottom =
            element.scrollHeight - element.scrollTop <= element.clientHeight + heightThreshold;

        if (atBottom && this.hasMore() && !this.isScrolling() && !this.isLoadingContents()) {
            this.page.update((p) => p + 1);
        }
    }

    /**
     * Load floating contents and optionally load contents assigned to a specific dealer if dealerId is passed.
     *
     * @param dealerId - An optional dealerId to fetch both floating contents and dealer's content.
     * @param append - Whether to append the new data to the existing contents list.
     */
    private loadFloatingAndDealerContents(dealerId: string | null, append = false): void {
        const loadingSignal = append ? this.isScrolling : this.isLoadingContents;

        this.mediaLibraryService
            .getFloatingContents(dealerId, { page: this.page(), limit: this.limit() })
            .pipe(
                withLoading(loadingSignal),
                catchError((err) => {
                    Logger.error('SelectAssetsModal', 'error fetching floating contents', err);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((res) => {
                this.mapDataToUi(res.data, append);
                this.hasMore.set(res.meta.pagination.hasNextPage);
            });
    }

    /**
     * Load dealer contents. Filters only dealer contents that are assigned or uploaded by the dealer.
     *
     * @param dealerId - The dealer id for filtering the contents assigned to the given id
     * @param append - Whether to append the new data to the existing contents list
     */
    private loadDealerContents(dealerId: string, append = false): void {
        const loadingSignal = append ? this.isScrolling : this.isLoadingContents;

        this.mediaLibraryService
            .getDealerContents(dealerId, { page: this.page(), limit: this.limit() })
            .pipe(
                withLoading(loadingSignal),
                catchError((err) => {
                    Logger.error('SelectAssetsModal', 'error fetching dealer contents', err);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((res) => {
                this.mapDataToUi(res.data, append);
                this.hasMore.set(res.meta.pagination.hasNextPage);
            });
    }

    /**
     * Determines whether the content is currently undergoing video conversion.
     * Excluding the socket because select-assets doesn't use WebSocket tracking.
     */
    protected isPendingConversion(content: UiThumbnailContent): boolean {
        const isVideo = content.type === 'video';
        if (!isVideo) return false;

        const hasMetadata =
            content.metadata !== null && content.metadata?.['optimizeVideo'] === true;
        return isConversionPending(content.isConverted, hasMetadata, content.fileName || '', false);
    }
}
